import axios from 'axios';
import API_CONFIG from '../config/api.config';

// ── Axios instance ────────────────────────────────────────────
const apiClient = axios.create({
  baseURL:        API_CONFIG.BASE_URL,
  timeout:        API_CONFIG.TIMEOUT,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor — attach JWT ─────────────────────────
// Token storage locations (checked in priority order):
//   1. sessionStorage['db_token']  — set by social login + real email login
//   2. localStorage['accessToken'] — legacy / refreshed tokens
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('db_token') || localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — handle token refresh ──────────────
apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const original = error.config;

    // Auto-refresh on 401 TOKEN_EXPIRED
    if (
      error.response?.status === 401 &&
      error.response?.data?.error?.code === 'TOKEN_EXPIRED' &&
      !original._retry
    ) {
      original._retry = true;
      try {
        const refreshToken = sessionStorage.getItem('db_refresh') || localStorage.getItem('refreshToken');
        const res = await axios.post(`${API_CONFIG.BASE_URL}/auth/refresh`, { refreshToken });
        const { accessToken } = res.data;
        sessionStorage.setItem('db_token', accessToken);
        localStorage.setItem('accessToken', accessToken); // keep legacy key in sync
        original.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(original);
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
      }
    }

    // Log full API error for debugging
    console.error('[API Error]', {
      url:    error.config?.url,
      status: error.response?.status,
      data:   error.response?.data,
      msg:    error.message,
    });

    // Return standardized error
    const apiErr = error.response?.data?.error || error.response?.data?.message;
    return Promise.reject(
      apiErr
        ? (typeof apiErr === 'string' ? { code: 'API_ERROR', message: apiErr } : apiErr)
        : { code: 'NETWORK_ERROR', message: error.message || 'Network error. Please try again.' }
    );
  }
);

export default apiClient;
